import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dashboard, loops, type LoopStatus } from "../../lib/api";
import { useUI } from "../../lib/store";
import { ThemeSwitcher } from "../shared/ThemeSwitcher";
import { Maestro, Search, Layout as LayoutIcon } from "../shared/Icon";
import { ModelCatalogModal } from "../settings/ModelCatalogModal";
import { IconButton } from "../shared/IconButton";
import { Menu, MenuItem, MenuLabel, MenuSeparator } from "../shared/Menu";
import { cn } from "../../lib/cn";
import { CommandPalette } from "./CommandPalette";
import { LAYOUT_PRESETS, type LayoutPreset } from "../../lib/layout";

interface Props {
  username?: string;
  onLogout: () => void;
}

export function Header({ username, onLogout }: Props) {
  const commandOpen = useUI((s) => s.commandOpen);
  const setCommandOpen = useUI((s) => s.setCommandOpen);

  const { data: live } = useQuery({
    queryKey: ["dashboard", "live"],
    queryFn: dashboard.live,
    refetchInterval: 4_000,
  });

  const running = live?.in_progress ?? 0;
  const needs = live?.needs_input ?? 0;
  const total = live?.total_live ?? 0;

  return (
    <header
      className={cn(
        "h-12 shrink-0 flex items-center gap-3 px-3",
        "bg-bg-panel border-b border-border-subtle",
      )}
    >
      <div className="flex items-center gap-2 pl-1">
        <Maestro size={20} className="text-accent" />
        <span className="text-heading text-text-primary tracking-tight">
          Maestro
        </span>
      </div>

      <div className="flex-1 flex justify-center px-2">
        <button
          type="button"
          onClick={() => setCommandOpen(true)}
          className={cn(
            "hidden md:flex items-center gap-2 h-7 px-2.5 w-full max-w-[480px]",
            "bg-bg-inset text-text-tertiary rounded-md",
            "border border-transparent hover:border-border-default hover:bg-bg-hover",
            "transition-colors duration-fast ease-out",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg-panel",
          )}
        >
          <Search />
          <span className="text-small flex-1 text-left">
            Search task transcripts…
          </span>
        </button>
      </div>

      <StageStatus running={running} needs={needs} total={total} />

      <div
        className="h-5 w-px bg-border-subtle mx-1"
        aria-hidden="true"
      />

      <LayoutMenu />

      <ThemeSwitcher />

      <div
        className="h-5 w-px bg-border-subtle mx-1"
        aria-hidden="true"
      />

      <UserMenu username={username} onLogout={onLogout} />

      <CommandPalette open={commandOpen} onClose={() => setCommandOpen(false)} />
    </header>
  );
}

function StageStatus({
  running,
  needs,
  total,
}: {
  running: number;
  needs: number;
  total: number;
}) {
  const detail =
    running > 0
      ? `${running} running`
      : needs > 0
        ? `${needs} need input`
        : "idle";
  return (
    <div
      className={cn(
        "hidden sm:flex items-center gap-2 h-7 px-2.5 rounded-full",
        "bg-bg-inset border border-border-subtle",
      )}
      title={`${total} live · ${running} running · ${needs} need input`}
    >
      <span className="relative inline-flex h-2 w-2">
        {running > 0 && (
          <span
            className="absolute inset-0 rounded-full motion-safe:animate-ping"
            style={{ background: "var(--status-running)", opacity: 0.55 }}
          />
        )}
        <span
          className="relative inline-flex h-2 w-2 rounded-full"
          style={{
            background:
              running > 0
                ? "var(--status-running)"
                : needs > 0
                  ? "var(--status-paused)"
                  : "var(--status-idle)",
          }}
        />
      </span>
      <span className="text-small text-text-tertiary whitespace-nowrap">
        {total} agent{total === 1 ? "" : "s"} · {detail}
      </span>
    </div>
  );
}

function UserMenu({
  username,
  onLogout,
}: {
  username?: string;
  onLogout: () => void;
}) {
  const initial = (username ?? "?").slice(0, 1).toUpperCase();
  const [modelsOpen, setModelsOpen] = useState(false);
  const qc = useQueryClient();
  const { data: loopStatus } = useQuery({
    queryKey: ["loops", "status"],
    queryFn: loops.status,
  });
  const envOn = loopStatus?.env_enabled ?? false;
  const runtimeOn = loopStatus?.runtime_enabled ?? false;
  const loopToggle = useMutation({
    mutationFn: (next: boolean) => loops.toggle(next),
    onMutate: async (next: boolean) => {
      await qc.cancelQueries({ queryKey: ["loops", "status"] });
      const prev = qc.getQueryData<LoopStatus>(["loops", "status"]);
      if (prev) {
        qc.setQueryData<LoopStatus>(["loops", "status"], {
          ...prev,
          runtime_enabled: next,
          master_enabled: prev.env_enabled && next,
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["loops", "status"], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["loops", "status"] }),
  });
  const loopsState = !envOn ? "env off" : runtimeOn ? "on" : "off";
  return (
    <>
    <Menu
      trigger={
        <button
          aria-label={`Account menu (${username ?? "user"})`}
          className={cn(
            "h-8 w-8 inline-flex items-center justify-center rounded-full",
            "bg-bg-inset text-text-secondary text-small font-medium",
            "hover:text-text-primary hover:bg-bg-hover transition-colors duration-fast ease-out",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg-panel",
          )}
        >
          {initial}
        </button>
      }
    >
      <MenuLabel>{username ?? "Signed in"}</MenuLabel>
      <MenuSeparator />
      <MenuItem onSelect={() => setModelsOpen(true)}>Models</MenuItem>
      <MenuItem
        onSelect={() => envOn && loopToggle.mutate(!runtimeOn)}
        disabled={!envOn}
        shortcut={loopsState}
      >
        Autonomous loops
      </MenuItem>
      <MenuSeparator />
      <MenuItem onSelect={onLogout}>Log out</MenuItem>
    </Menu>
    {modelsOpen && <ModelCatalogModal onClose={() => setModelsOpen(false)} />}
    </>
  );
}

function LayoutMenu() {
  const setPreset = useUI((s) => s.applyLayoutPreset);
  const current = useUI((s) => s.layoutPreset);
  return (
    <Menu
      trigger={
        <IconButton icon={<LayoutIcon />} label="Layout" size="sm" />
      }
    >
      <MenuLabel>Layout presets</MenuLabel>
      {(Object.keys(LAYOUT_PRESETS) as LayoutPreset[]).map((key) => (
        <MenuItem
          key={key}
          onSelect={() => setPreset(key)}
          shortcut={current === key ? "•" : undefined}
        >
          {LAYOUT_PRESETS[key].label}
        </MenuItem>
      ))}
    </Menu>
  );
}
